import { Injectable, OnDestroy } from '@angular/core'
import { Subject } from 'rxjs'
import { Feature, Polygon } from 'geojson'
import MapboxDraw from '@mapbox/mapbox-gl-draw'
import { MapService } from 'src/app/core/services/map.service'

const DRAW_ACTIVE_FILL_COLOR = '#F2A3A3'
const DRAW_ACTIVE_STROKE_COLOR = '#D96868'
const DRAW_INACTIVE_FILL_COLOR = '#D97777'
const DRAW_INACTIVE_STROKE_COLOR = '#B54747'

const DRAW_STYLES = [
  {
    id: 'gl-draw-polygon-fill-inactive',
    type: 'fill',
    filter: ['all', ['==', 'active', 'false'], ['==', '$type', 'Polygon']],
    paint: {
      'fill-color': DRAW_INACTIVE_FILL_COLOR,
      'fill-outline-color': DRAW_INACTIVE_STROKE_COLOR,
      'fill-opacity': 0.22,
    },
  },
  {
    id: 'gl-draw-polygon-fill-active',
    type: 'fill',
    filter: ['all', ['==', 'active', 'true'], ['==', '$type', 'Polygon']],
    paint: {
      'fill-color': DRAW_ACTIVE_FILL_COLOR,
      'fill-outline-color': DRAW_ACTIVE_STROKE_COLOR,
      'fill-opacity': 0.18,
    },
  },
  {
    id: 'gl-draw-polygon-stroke-inactive',
    type: 'line',
    filter: ['all', ['==', 'active', 'false'], ['==', '$type', 'Polygon']],
    layout: {
      'line-cap': 'round',
      'line-join': 'round',
    },
    paint: {
      'line-color': DRAW_INACTIVE_STROKE_COLOR,
      'line-width': 3,
    },
  },
  {
    id: 'gl-draw-polygon-stroke-active',
    type: 'line',
    filter: ['all', ['==', 'active', 'true'], ['==', '$type', 'Polygon']],
    layout: {
      'line-cap': 'round',
      'line-join': 'round',
    },
    paint: {
      'line-color': DRAW_ACTIVE_STROKE_COLOR,
      'line-dasharray': [0.25, 2],
      'line-width': 3,
    },
  },
  {
    id: 'gl-draw-line-inactive',
    type: 'line',
    filter: ['all', ['==', 'active', 'false'], ['==', '$type', 'LineString']],
    layout: {
      'line-cap': 'round',
      'line-join': 'round',
    },
    paint: {
      'line-color': DRAW_INACTIVE_STROKE_COLOR,
      'line-width': 3,
    },
  },
  {
    id: 'gl-draw-line-active',
    type: 'line',
    filter: ['all', ['==', 'active', 'true'], ['==', '$type', 'LineString']],
    layout: {
      'line-cap': 'round',
      'line-join': 'round',
    },
    paint: {
      'line-color': DRAW_ACTIVE_STROKE_COLOR,
      'line-dasharray': [0.25, 2],
      'line-width': 3,
    },
  },
  {
    id: 'gl-draw-point-outer',
    type: 'circle',
    filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point']],
    paint: {
      'circle-radius': 6,
      'circle-color': '#FFFFFF',
    },
  },
  {
    id: 'gl-draw-point-inner',
    type: 'circle',
    filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point']],
    paint: {
      'circle-radius': 4,
      'circle-color': DRAW_ACTIVE_STROKE_COLOR,
    },
  },
]

export type DrawnPolygon = {
  polygon: Feature<Polygon>
  center: { lng: number; lat: number }
}

@Injectable()
export class DrawService implements OnDestroy {
  public isDrawMode = false

  private draw: MapboxDraw | null = null
  private completedFeatureId: string | undefined

  private readonly PREVIEW_SOURCE_ID = 'draw-preview-src'
  private readonly PREVIEW_FILL_LAYER_ID = 'draw-preview-fill'
  private readonly PREVIEW_LINE_LAYER_ID = 'draw-preview-line'
  public polygonDrawn$ = new Subject<DrawnPolygon>()

  constructor(private mapService: MapService) {}

  private readonly onDrawCreate = (e: any) => {
    if (!this.isDrawMode || !this.draw) return
    const lastFeature = e.features?.[0]
    if (!lastFeature) return
    if (
      lastFeature.geometry.type === 'Polygon' &&
      lastFeature.geometry.coordinates[0].length >= 4
    ) {
      this.completedFeatureId = String(lastFeature.id)
      this.finishDrawing(lastFeature as Feature<Polygon>, e.lngLat)
    }
  }

  private ensurePreviewLayer(): void {
    if (this.mapService.map.getSource(this.PREVIEW_SOURCE_ID)) return
    this.mapService.map.addSource(this.PREVIEW_SOURCE_ID, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    })
    this.mapService.map.addLayer({
      id: this.PREVIEW_FILL_LAYER_ID,
      type: 'fill',
      source: this.PREVIEW_SOURCE_ID,
      paint: { 'fill-color': DRAW_INACTIVE_FILL_COLOR, 'fill-opacity': 0.22 },
    })
    this.mapService.map.addLayer({
      id: this.PREVIEW_LINE_LAYER_ID,
      type: 'line',
      source: this.PREVIEW_SOURCE_ID,
      paint: { 'line-color': DRAW_INACTIVE_STROKE_COLOR, 'line-width': 3 },
    })
  }

  private setPreviewPolygon(polygon: Feature<Polygon> | null): void {
    this.ensurePreviewLayer()
    const source = this.mapService.map.getSource(this.PREVIEW_SOURCE_ID) as any
    if (!source) return
    source.setData({
      type: 'FeatureCollection',
      features: polygon ? [polygon] : [],
    })
  }

  ngOnDestroy(): void {
    this.destroyDraw()
  }

  public toggleDrawMode(): void {
    if (this.isDrawMode) {
      this.exitDrawMode(false)
    } else {
      this.enterDrawMode()
    }
  }

  public enterDrawMode(): void {
    this.isDrawMode = true
    this.mapService.map.getCanvas().style.cursor = 'crosshair'

    if (!this.draw) {
      this.draw = new MapboxDraw({
        displayControlsDefault: false,
        styles: DRAW_STYLES as any,
      })
      // MapboxDraw is compatible with MapLibre, cast map to any to bypass type checking
      this.mapService.map.addControl(this.draw as any)
    }

    this.clearCompletedPolygon()
    this.draw.changeMode('draw_polygon')
    this.mapService.map.on('draw.create', this.onDrawCreate)
  }

  exitDrawMode(removePolygon: boolean): void {
    this.isDrawMode = false
    this.mapService.map.getCanvas().style.cursor = ''
    this.mapService.map.off('draw.create', this.onDrawCreate)

    if (this.draw) {
      this.draw.changeMode('simple_select')
      this.draw.deleteAll()
    }

    if (removePolygon) {
      this.clearCompletedPolygon()
      this.draw?.deleteAll()
    }
  }

  /** Call when the dialog closes to remove the preview and let the GeoJSON layer take over. */
  public clearLastPolygon(): void {
    this.setPreviewPolygon(null)
  }

  private finishDrawing(polygon: Feature<Polygon>, center: any): void {
    this.exitDrawMode(false)

    // Immediately delete from MapboxDraw and show as static preview instead — not draggable.
    this.clearCompletedPolygon()
    this.setPreviewPolygon(polygon)

    const centerPoint: { lng: number; lat: number } = center ?? {
      lng: polygon.geometry.coordinates[0][0][0],
      lat: polygon.geometry.coordinates[0][0][1],
    }

    this.polygonDrawn$.next({ polygon, center: centerPoint })
  }

  private clearCompletedPolygon(): void {
    if (!this.draw || this.completedFeatureId === undefined) return
    this.draw.delete(this.completedFeatureId)
    this.completedFeatureId = undefined
  }

  private isPointInsidePolygon(lng: number, lat: number, polygon: any): boolean {
    const ring = polygon?.geometry?.coordinates?.[0]
    if (!Array.isArray(ring) || ring.length < 3) return false

    let inside = false
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const xi = ring[i][0],
        yi = ring[i][1]
      const xj = ring[j][0],
        yj = ring[j][1]
      const intersects = yi > lat !== yj > lat && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi
      if (intersects) inside = !inside
    }
    return inside
  }

  private destroyDraw(): void {
    this.mapService.map.getCanvas().style.cursor = ''
    this.mapService.map.off('draw.create', this.onDrawCreate)

    if (this.draw) {
      this.draw.deleteAll()
      // MapboxDraw is compatible with MapLibre, cast to any to bypass type checking
      this.mapService.map.removeControl(this.draw as any)
      this.draw = null
    }
    this.completedFeatureId = undefined
    this.isDrawMode = false

    if (this.mapService.map.getLayer(this.PREVIEW_LINE_LAYER_ID)) {
      this.mapService.map.removeLayer(this.PREVIEW_LINE_LAYER_ID)
    }
    if (this.mapService.map.getLayer(this.PREVIEW_FILL_LAYER_ID)) {
      this.mapService.map.removeLayer(this.PREVIEW_FILL_LAYER_ID)
    }
    if (this.mapService.map.getSource(this.PREVIEW_SOURCE_ID)) {
      this.mapService.map.removeSource(this.PREVIEW_SOURCE_ID)
    }
  }
}
